import { theme } from '@Guidelines/Theme/theme';
import useCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useCurrentFacility';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import buildSelectorModel from '@UI/Guidelines/Shell/AppShell/AppHeader/FacilitySelector/Helpers/buildSelectorModel';
import { css } from '@emotion/react';
import { Building2, ChevronDown, Landmark } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, Spinner } from '@verclam/ui-kit';
import type { MenuHandle, MenuItem } from '@verclam/ui-kit';

const pillStyle = css`
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.5rem 1rem;
	border-radius: ${theme.radii.pill};
	font-family: ${theme.fonts.body};
	font-weight: ${theme.fontWeights.medium};
	font-size: ${theme.fontSizes.medium};
	max-width: 12rem;
	min-width: 0;

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		max-width: 9rem;
		padding: 0.5rem 0.75rem;
	}
`;

const activePillStyle = css`
	border: none;
	background-color: ${theme.appBar.facilitySwitcher.background};
	color: ${theme.appBar.facilitySwitcher.text};
	cursor: pointer;

	&:focus-visible {
		outline: 2px solid ${theme.colors.accent};
		outline-offset: 2px;
	}
`;

const staticPillStyle = css`
	background-color: ${theme.appBar.facilitySwitcher.background};
	color: ${theme.appBar.facilitySwitcher.text};
`;

const mutedPillStyle = css`
	background-color: ${theme.appBar.navChip.background};
	color: ${theme.appBar.navChip.text};
`;

const labelStyle = css`
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

/**
 * Organization and facility switcher, rendered in `AppHeader`. Every facility and every organization
 * without a facility is a selectable entry; picking an empty organization lands on `/facilities`.
 * Falls back to inert text whenever there is nothing to open: no organization at all, or exactly one
 * selectable entry and it is the current context. Renders nothing once `startupError` is set:
 * `AppMain` already shows the real diagnosis below the header. Stays visible on narrow viewports,
 * where the pill shrinks and truncates its label.
 */
const FacilitySelector: React.FC = () => {
	const { organizations, currentOrganization, selectOrganization, startupError } = useCurrentOrganization();
	const { currentFacility, isResolved, selectFacility } = useCurrentFacility();
	const navigate = useNavigate();
	const menuRef = React.useRef<MenuHandle>(null);

	const allFacilities = React.useMemo(
		() => organizations.flatMap((organization) => organization.facilities),
		[organizations],
	);

	const emptyOrganizations = React.useMemo(
		() => organizations.filter((organization) => organization.facilities.length === 0),
		[organizations],
	);

	const selectableCount = allFacilities.length + emptyOrganizations.length;

	const handleToggle = React.useCallback((event: React.SyntheticEvent) => {
		menuRef.current?.toggle(event);
	}, []);

	const handleSelectFacility = React.useCallback(
		(facilityId: number, organizationId: number) => {
			selectOrganization(organizationId);
			selectFacility(facilityId);
		},
		[selectOrganization, selectFacility],
	);

	const handleSelectEmptyOrganization = React.useCallback(
		(organizationId: number) => {
			selectOrganization(organizationId);
			navigate('/facilities');
		},
		[selectOrganization, navigate],
	);

	const model = React.useMemo<readonly MenuItem[]>(
		() =>
			buildSelectorModel({
				organizations,
				selectFacilityEntry: handleSelectFacility,
				selectEmptyOrganizationEntry: handleSelectEmptyOrganization,
			}),
		[organizations, handleSelectFacility, handleSelectEmptyOrganization],
	);

	const menuPt = React.useMemo<React.ComponentProps<typeof Menu>['pt']>(
		() => ({
			root: {
				style: {
					minWidth: '12rem',
					maxHeight: '14rem',
					overflowY: 'auto',
				},
			},
		}),
		[],
	);

	if (!isResolved) {
		return (
			<Spinner
				aria-label="Résolution de l'agence courante"
				pt={{ root: { style: { width: '1.25rem', height: '1.25rem' } } }}
			/>
		);
	}

	// The startup gate (`AppMain`) already renders the real diagnosis below the header: a "Aucune
	// agence" pill here would read as a second, misleading answer next to the true error.
	if (startupError) {
		return null;
	}

	if (organizations.length === 0) {
		return (
			<span css={[pillStyle, mutedPillStyle]}>
				<Building2 size={16} />
				Aucune agence
			</span>
		);
	}

	const isCurrentOrganizationEmpty =
		currentOrganization !== null && currentOrganization.facilities.length === 0;

	// The current context is unresolved (retired identifier mid-session) when it is neither a facility
	// nor an empty organization: that always requires an explicit choice.
	if (selectableCount === 1 && (currentFacility !== null || isCurrentOrganizationEmpty)) {
		const InertIcon = currentFacility !== null ? Building2 : Landmark;
		const inertLabel = currentFacility?.name ?? currentOrganization?.name;

		return (
			<span css={[pillStyle, staticPillStyle]}>
				<InertIcon size={16} />
				<span css={labelStyle}>{inertLabel}</span>
			</span>
		);
	}

	const isTriggerOrganization = currentFacility === null && isCurrentOrganizationEmpty;
	const TriggerIcon = isTriggerOrganization ? Landmark : Building2;
	const triggerLabel =
		currentFacility?.name ??
		(isTriggerOrganization ? currentOrganization?.name : undefined) ??
		'Choisir une agence';

	return (
		<React.Fragment>
			<button
				type="button"
				css={[pillStyle, activePillStyle]}
				onClick={handleToggle}
				aria-label="Changer d'agence ou de structure"
			>
				<TriggerIcon size={16} />
				<span css={labelStyle}>{triggerLabel}</span>
				<ChevronDown size={16} />
			</button>
			<Menu
				ref={menuRef}
				popup
				model={model}
				ariaLabel="Agences et structures"
				popupAlignment="right"
				pt={menuPt}
			/>
		</React.Fragment>
	);
};

export default FacilitySelector;
